import axios from 'axios';
import { TenantInfo, TenantUser } from '../context/TenantContext';
import { getApiClient } from './apiService';

export class TenantService {
  private static get api() {
    return getApiClient();
  }

  /**
   * Get current tenant information
   */
  static async getTenantInfo(): Promise<TenantInfo> {
    try {
      const response = await this.api.get<TenantInfo>('/tenant/info');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get all users in current tenant
   */
  static async getTenantUsers(): Promise<TenantUser[]> {
    try {
      const response = await this.api.get<TenantUser[]>('/tenant/users');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update tenant information
   */
  static async updateTenant(data: Partial<TenantInfo>): Promise<TenantInfo> {
    try {
      const response = await this.api.patch<TenantInfo>('/tenant/info', data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Invite a new user to tenant
   */
  static async inviteUser(data: {
    email: string;
    name: string;
    role: TenantUser['role'];
  }): Promise<TenantUser> {
    try {
      const response = await this.api.post<TenantUser>('/tenant/users/invite', data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Remove user from tenant
   */
  static async removeUser(userId: string): Promise<void> {
    try {
      await this.api.delete(`/tenant/users/${userId}`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update user role in tenant
   */
  static async updateUserRole(userId: string, role: TenantUser['role']): Promise<TenantUser> {
    try {
      const response = await this.api.patch<TenantUser>(`/tenant/users/${userId}/role`, { role });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get subscription details
   */
  static async getSubscription() {
    try {
      const response = await this.api.get('/tenant/subscription');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update subscription
   */
  static async updateSubscription(data: any) {
    try {
      const response = await this.api.patch('/tenant/subscription', data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get usage metrics for tenant
   */
  static async getUsageMetrics(days: number = 30) {
    try {
      const response = await this.api.get('/tenant/metrics/usage', {
        params: { days },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Error handler
   */
  private static handleError(error: any): Error {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.message || error.message;
      return new Error(message);
    }
    return error instanceof Error ? error : new Error('An error occurred');
  }
}
